import moment from "moment";
import { useSelector } from "react-redux";
import { RootState } from "src/redux";
import { useTranslate } from "@tolgee/react";
import { formatAccessibleDateValue } from "src/common/helpers";

interface AccessibleDateProps {
  date: string | moment.Moment;
  displayStyle?: "numeric" | "abbreviated";
  withTime?: boolean;
}

export function AccessibleDate({
  date,
  displayStyle = "numeric",
  withTime = false,
}: AccessibleDateProps) {
  const language = useSelector((state: RootState) => state.user.language);
  const userDateFormat = useSelector(
    (state: RootState) => state.user.data?.dateFormat,
  );
  const userDateTimeFormat = useSelector(
    (state: RootState) => state.user.data?.dateTimeFormat,
  );
  const parsed = moment.isMoment(date) ? date : moment(date);

  const { t } = useTranslate();

  if (!parsed.isValid()) {
    return (
      <>
        <span aria-hidden="true">
          {t({ key: "accessibleDate.na", defaultValue: "N/A" })}
        </span>
        <span className="visually-hidden">
          {t({
            key: "accessibleDate.notAvailable",
            defaultValue: "Not available",
          })}
        </span>
      </>
    );
  }

  // re-tagged as UTC so Intl won't re-shift them into the viewer's local zone when formatting.
  const dateForFormatting = new Date(
    Date.UTC(
      parsed.year(),
      parsed.month(),
      parsed.date(),
      parsed.hour(),
      parsed.minute(),
      parsed.second(),
      parsed.millisecond(),
    ),
  );
  const userFormat = withTime ? userDateTimeFormat : userDateFormat;
  const visible = formatAccessibleDateValue(parsed, {
    language,
    userFormat,
    displayStyle,
    withTime,
  });
  const screenReader = new Intl.DateTimeFormat(language, {
    year: "numeric",
    month: "long",
    day: "numeric",
    ...(withTime && { hour: "numeric", minute: "2-digit" }),
    timeZone: "UTC",
  }).format(dateForFormatting);

  return (
    <>
      <span aria-hidden="true">{visible}</span>
      <span className="visually-hidden">{screenReader}</span>
    </>
  );
}
